import dayjs from "dayjs";
import type { Job } from "@domain/CRM/job";
import type { Shift } from "../types/Shift";
import type { SelectionToken } from "./mapSelection";
import { dropoffSelectionToken } from "./mapSelection";
import type { RowKey } from "./stopPlanning";
import { jobRowKey, parseRowKey } from "./stopPlanning";

export interface RolloverCandidates {
	/** Unsampled jobs to roll over, in display order. */
	eligible: Job[];
	/** Jobs excluded because they are already sampled (MC: "Sample Date" set ≙ finished). */
	skippedSampled: Job[];
}

/**
 * Jobs to roll over from a shift's ordered job list: everything — or, given `fromJobId`,
 * that row and everything after it in display order — partitioned by `dateSampled`.
 * Unknown `fromJobId` rolls nothing (index-form mirror of MC's undefined-Order guard).
 */
export function rolloverCandidates(orderedJobs: readonly Job[], fromJobId?: string): RolloverCandidates {
	let slice = [...orderedJobs];
	if (fromJobId !== undefined) {
		const fromIdx = slice.findIndex((j) => j.id === fromJobId);
		slice = fromIdx === -1 ? [] : slice.slice(fromIdx);
	}
	return {
		eligible: slice.filter((j) => !j.dateSampled),
		skippedSampled: slice.filter((j) => j.dateSampled),
	};
}

/**
 * Rollover destination options: every shift except the source, ordered by date descending
 * (startDate with endDate fallback; dateless shifts last). Upcoming and past interleave on
 * one axis rather than forming separate asc/desc blocks.
 */
export function rolloverDestinations(shifts: Shift[], sourceShiftId: string): Shift[] {
	const dateOf = (s: Shift) => s.startDate?.getTime() ?? s.endDate?.getTime() ?? Number.NEGATIVE_INFINITY;
	return shifts.filter((s) => s.id !== sourceShiftId).sort((a, b) => dateOf(b) - dateOf(a));
}

/** The identifying parameters of a shift, as MC's duplicate guard sees them. */
export interface ShiftParams {
	robotId?: string;
	operatorId?: string;
	rigId?: string;
	scheduledDate?: Date;
}

/**
 * MC's duplicate-parameter guard for "create new shift": true when an existing shift has the
 * same robot + operator + rig on the same calendar day (time-of-day ignored). Existing shifts
 * with missing fields never match; an incomplete candidate never flags.
 */
export function hasDuplicateShiftParams(existing: readonly ShiftParams[], candidate: ShiftParams): boolean {
	const { robotId, operatorId, rigId, scheduledDate } = candidate;
	if (!robotId || !operatorId || !rigId || !scheduledDate) return false;
	const day = dayjs(scheduledDate);
	return existing.some(
		(s) =>
			s.robotId === robotId &&
			s.operatorId === operatorId &&
			s.rigId === rigId &&
			s.scheduledDate != null &&
			day.isSame(s.scheduledDate, "day"),
	);
}

/** How many calendar days back an ended shift stays "recent" (rollover source; MC-parity
 *  inline past shifts). The scheduling fetch cutoff derives from this — keep them coupled. */
export const RECENT_ENDED_WINDOW_DAYS = 7;

/**
 * Shifts that already ended (complement of `upcomingShifts`: `endDate < startOfDay(now)`)
 * within the last `days` calendar days, most-recent-first. The RECENT rollover-source group.
 */
export function recentEndedShifts(shifts: Shift[], now: Date, days = RECENT_ENDED_WINDOW_DAYS): Shift[] {
	const todayStart = dayjs(now).startOf("day");
	const cutoff = todayStart.valueOf();
	const windowStart = todayStart.subtract(days, "day").valueOf();
	return shifts
		.filter((s) => {
			const end = s.endDate?.getTime();
			return end !== undefined && end < cutoff && end >= windowStart;
		})
		.sort((a, b) => (b.endDate?.getTime() ?? 0) - (a.endDate?.getTime() ?? 0));
}

/**
 * The plan a dropoffs-inclusive rollover stages (extension beyond MC — MC rolls jobs
 * only). Walks the source card's displayed rows in order: eligible jobs stay as job
 * tokens; stop rows (committed junctions AND staged new stops) become `dropoff:` tokens
 * via their master, so the staging macro materializes fresh stops on the destination.
 * A roll is a MOVE, matching the rolled jobs: the rolled source stops come back in
 * `rolledStopIds` / `rolledNewStopKeys` so the caller stages their removal (struck rows)
 * on the source. Stops with no resolvable master are skipped entirely — neither rolled
 * nor struck.
 */
export interface RolloverTokenPlan {
	tokens: SelectionToken[];
	/** Committed source stops that rolled — stage their removal on the source. */
	rolledStopIds: string[];
	/** Staged-new source stops that rolled — un-stage them from the source. */
	rolledNewStopKeys: string[];
}

export function rolloverTokens(
	rowKeys: readonly RowKey[],
	eligibleJobIds: ReadonlySet<string>,
	stopDropoffIdByStopId: ReadonlyMap<string, string>,
	newStopDropoffIdByKey: ReadonlyMap<string, string>,
): RolloverTokenPlan {
	const tokens: SelectionToken[] = [];
	const rolledStopIds: string[] = [];
	const rolledNewStopKeys: string[] = [];
	for (const key of rowKeys) {
		const row = parseRowKey(key);
		if (row.kind === "job") {
			if (eligibleJobIds.has(row.id)) tokens.push(jobRowKey(row.id));
			continue;
		}
		const dropoffId = row.kind === "stop" ? stopDropoffIdByStopId.get(row.id) : newStopDropoffIdByKey.get(row.id);
		if (!dropoffId) continue;
		tokens.push(dropoffSelectionToken(dropoffId));
		(row.kind === "stop" ? rolledStopIds : rolledNewStopKeys).push(row.id);
	}
	return { tokens, rolledStopIds, rolledNewStopKeys };
}
